import React, { useState, useEffect, useCallback } from 'react';
import {
    CheckCircle2,
    AlertCircle,
    KeyRound,
    Sparkles,
    ChevronLeft,
    Loader2,
} from 'lucide-react';
import {
    idibuApi,
    type IdibuOrgConfig,
    type IdibuPortal,
} from '../services/idibu.api';
import BoardMarketplace from './BoardMarketplace';
import BoardSubscribeDrawer from './BoardSubscribeDrawer';
import { useIdibuCountries } from '../hooks/useIdibuCountries';
import { IDIBU_READ_ONLY } from '../demo/demo-flag';

type ViewState = 'loading' | 'disconnected' | 'connected' | 'error';
type ConnectMode = 'manual' | 'provision' | null;

export default function IdibuSettingsPanel() {
    const [viewState, setViewState] = useState<ViewState>('loading');
    const [config, setConfig] = useState<IdibuOrgConfig | null>(null);
    const [partnerAvailable, setPartnerAvailable] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [connectMode, setConnectMode] = useState<ConnectMode>(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);
    const [selectedPortal, setSelectedPortal] = useState<IdibuPortal | null>(null);
    const [subscribedIds, setSubscribedIds] = useState<Set<number>>(new Set());
    const [marketplaceKey, setMarketplaceKey] = useState(0);

    const [manualHash, setManualHash] = useState('');

    const [provisionForm, setProvisionForm] = useState({
        companyName: '',
        adminFirstname: '',
        adminLastname: '',
        adminEmail: '',
        username: '',
        password: '',
        country: 'GB',
        postcode: '',
        phone: '',
        website: '',
    });

    const loadConfig = useCallback(async () => {
        try {
            const { data } = await idibuApi.getConfig();
            setConfig(data.config);
            setPartnerAvailable(data.partnerAvailable);
            setViewState(data.config?.isActive ? 'connected' : 'disconnected');
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to load config');
            setViewState('error');
        }
    }, []);

    const loadSubscribedIds = useCallback(async () => {
        try {
            const { data } = await idibuApi.listSubscribedPortals();
            setSubscribedIds(new Set((data.portals || []).map((p: IdibuPortal) => Number(p.id))));
        } catch {
            /* non-critical */
        }
    }, []);

    useEffect(() => {
        loadConfig();
    }, [loadConfig]);

    useEffect(() => {
        if (viewState === 'connected') loadSubscribedIds();
    }, [viewState, loadSubscribedIds]);

    const handleDrawerChanged = useCallback(() => {
        loadSubscribedIds();
        setMarketplaceKey((k) => k + 1);
    }, [loadSubscribedIds]);

    const handleManualConnect = async () => {
        if (!manualHash.trim()) return;
        setSaving(true);
        setError(null);
        try {
            const { data } = await idibuApi.updateConfig({ accountHash: manualHash.trim() });
            setConfig(data.config);
            setViewState('connected');
            setConnectMode(null);
            setManualHash('');
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to connect');
        } finally {
            setSaving(false);
        }
    };

    const handleProvision = async () => {
        const { companyName, adminFirstname, adminLastname, adminEmail, username, password } = provisionForm;
        if (!companyName || !adminFirstname || !adminLastname || !adminEmail || !username || !password) {
            setError('All required fields must be filled');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const { data } = await idibuApi.provisionAccount(provisionForm);
            setConfig(data.config);
            setViewState('connected');
            setConnectMode(null);
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Provisioning failed');
        } finally {
            setSaving(false);
        }
    };

    const handleReconnect = async () => {
        setSaving(true);
        setError(null);
        try {
            const { data } = await idibuApi.reconnect();
            setConfig(data.config);
            setViewState('connected');
            setTestResult(null);
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to reconnect');
        } finally {
            setSaving(false);
        }
    };

    const handleDisconnect = async () => {
        if (!window.confirm('Disconnect job-board sync? Your credentials will stay saved for easy reconnection.')) return;
        setSaving(true);
        setError(null);
        try {
            await idibuApi.disconnectConfig();
            await loadConfig();
            setTestResult(null);
        } catch (err: any) {
            setError(err?.response?.data?.error || err.message || 'Failed to disconnect');
        } finally {
            setSaving(false);
        }
    };

    const handleTestConnection = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const { data } = await idibuApi.listSubscribedPortals();
            const count = data.portals?.length ?? 0;
            setTestResult({ ok: true, message: `Connection healthy — ${count} subscribed board${count === 1 ? '' : 's'}.` });
        } catch (err: any) {
            setTestResult({ ok: false, message: err?.response?.data?.error || err.message || 'Connection test failed' });
        } finally {
            setTesting(false);
        }
    };

    const updateProvisionField = (field: string, value: string) => {
        setProvisionForm((prev) => ({ ...prev, [field]: value }));
    };

    if (viewState === 'loading') {
        return (
            <div className="ib-card ib-card--placeholder">
                <Loader2 size={16} className="spin" />
                <span>Loading job-board configuration…</span>
            </div>
        );
    }

    return (
        <>
            <style>{PANEL_CSS}</style>

            {error && (
                <div className="ib-banner ib-banner--error" role="alert">
                    <AlertCircle size={15} />
                    <span>{error}</span>
                    <button type="button" onClick={() => setError(null)} aria-label="Dismiss">×</button>
                </div>
            )}

            {viewState === 'connected' && config && (
                <>
                    <ConnectedCard
                        config={config}
                        subscribedCount={subscribedIds.size}
                        testing={testing}
                        testResult={testResult}
                        saving={saving}
                        onTest={handleTestConnection}
                        onDisconnect={handleDisconnect}
                    />
                    <BoardMarketplace
                        key={marketplaceKey}
                        onBoardClick={setSelectedPortal}
                    />
                    {selectedPortal && (
                        <BoardSubscribeDrawer
                            portal={selectedPortal}
                            isSubscribed={subscribedIds.has(Number(selectedPortal.id))}
                            onClose={() => setSelectedPortal(null)}
                            onChanged={handleDrawerChanged}
                        />
                    )}
                </>
            )}

            {(viewState === 'disconnected' || viewState === 'error') && (
                <DisconnectedView
                    connectMode={connectMode}
                    partnerAvailable={partnerAvailable}
                    saving={saving}
                    manualHash={manualHash}
                    provisionForm={provisionForm}
                    hasStoredHash={config?.hasStoredHash || false}
                    storedHashDisplay={config?.accountHash || null}
                    onSetConnectMode={setConnectMode}
                    onManualHashChange={setManualHash}
                    onProvisionFieldChange={updateProvisionField}
                    onManualConnect={handleManualConnect}
                    onProvision={handleProvision}
                    onReconnect={handleReconnect}
                />
            )}
        </>
    );
}

// ─── Connected status card ───────────────────────────────────────────────────

function ConnectedCard({
    config,
    subscribedCount,
    testing,
    testResult,
    saving,
    onTest,
    onDisconnect,
}: {
    config: IdibuOrgConfig;
    subscribedCount: number;
    testing: boolean;
    testResult: { ok: boolean; message: string } | null;
    saving: boolean;
    onTest: () => void;
    onDisconnect: () => void;
}) {
    const connectedDate = config.connectedAt
        ? new Date(config.connectedAt).toLocaleDateString(undefined, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
          })
        : null;

    const boardCountLabel = `${subscribedCount} board${subscribedCount === 1 ? '' : 's'} active`;

    return (
        <div className="ib-card ib-status">
            <div className="ib-status__icon" aria-hidden>
                <CheckCircle2 size={20} />
            </div>
            <div className="ib-status__copy">
                <strong>Job-board sync connected</strong>
                <span>
                    Account <code>{config.accountHash || '****'}</code>
                    {connectedDate ? <> · Connected {connectedDate}</> : null}
                    <> · {boardCountLabel}</>
                    {config.provisionedByPartner ? <span className="ib-tag">Provisioned</span> : null}
                </span>
                {testResult && (
                    <span className={`ib-status__test ${testResult.ok ? 'ok' : 'err'}`}>
                        {testResult.message}
                    </span>
                )}
            </div>
            <div className="ib-status__actions">
                <button
                    type="button"
                    className="ib-btn ib-btn--ghost"
                    onClick={onTest}
                    disabled={testing}
                >
                    {testing ? 'Testing…' : 'Test connection'}
                </button>
                <button
                    type="button"
                    className="ib-btn ib-btn--danger-ghost"
                    onClick={onDisconnect}
                    disabled={saving || IDIBU_READ_ONLY}
                    title={IDIBU_READ_ONLY ? 'Editing is disabled in demo mode' : undefined}
                >
                    {saving ? 'Disconnecting…' : 'Disconnect'}
                </button>
            </div>
        </div>
    );
}

// ─── Disconnected view ───────────────────────────────────────────────────────

function DisconnectedView({
    connectMode,
    partnerAvailable,
    saving,
    manualHash,
    provisionForm,
    hasStoredHash,
    storedHashDisplay,
    onSetConnectMode,
    onManualHashChange,
    onProvisionFieldChange,
    onManualConnect,
    onProvision,
    onReconnect,
}: {
    connectMode: ConnectMode;
    partnerAvailable: boolean;
    saving: boolean;
    manualHash: string;
    provisionForm: Record<string, string>;
    hasStoredHash: boolean;
    storedHashDisplay: string | null;
    onSetConnectMode: (mode: ConnectMode) => void;
    onManualHashChange: (v: string) => void;
    onProvisionFieldChange: (field: string, value: string) => void;
    onManualConnect: () => void;
    onProvision: () => void;
    onReconnect: () => void;
}) {
    if (!connectMode) {
        return (
            <div className="ib-card ib-disconnected">
                {hasStoredHash ? (
                    <>
                        <div className="ib-status__icon ib-status__icon--muted" aria-hidden>
                            <AlertCircle size={20} />
                        </div>
                        <div className="ib-status__copy">
                            <strong>Disconnected</strong>
                            <span>
                                Your previous job-board account ({storedHashDisplay}) is still saved. Reconnect to resume,
                                or connect a different account.
                            </span>
                        </div>
                        <div className="ib-status__actions">
                            <button
                                type="button"
                                className="ib-btn ib-btn--primary"
                                onClick={onReconnect}
                                disabled={saving}
                            >
                                {saving ? 'Reconnecting…' : 'Reconnect'}
                            </button>
                            <button type="button" className="ib-btn ib-btn--ghost" onClick={() => onSetConnectMode('manual')}>
                                Use different hash
                            </button>
                            {partnerAvailable && (
                                <button type="button" className="ib-btn ib-btn--ghost" onClick={() => onSetConnectMode('provision')}>
                                    Provision new
                                </button>
                            )}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="ib-disconnected__copy">
                            <strong>Connect job-board sync</strong>
                            <span>
                                Publish jobs to 100+ external boards from one place. Connect an existing
                                account or provision a new one.
                            </span>
                        </div>
                        <div className="ib-options">
                            <button type="button" className="ib-option" onClick={() => onSetConnectMode('manual')}>
                                <span className="ib-option__icon"><KeyRound size={18} /></span>
                                <span className="ib-option__title">Connect existing account</span>
                                <span className="ib-option__desc">Enter an account hash you already have.</span>
                            </button>
                            {partnerAvailable && (
                                <button type="button" className="ib-option" onClick={() => onSetConnectMode('provision')}>
                                    <span className="ib-option__icon"><Sparkles size={18} /></span>
                                    <span className="ib-option__title">Provision new account</span>
                                    <span className="ib-option__desc">Create a new client account automatically.</span>
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    }

    if (connectMode === 'manual') {
        return (
            <div className="ib-card ib-form-card">
                <div className="ib-form-card__header">
                    <h3>Connect existing account</h3>
                    <button type="button" className="ib-btn ib-btn--ghost" onClick={() => onSetConnectMode(null)}>
                        <ChevronLeft size={14} /> Back
                    </button>
                </div>
                <label className="ib-field">
                    <span>Account hash</span>
                    <input
                        type="text"
                        value={manualHash}
                        onChange={(e) => onManualHashChange(e.target.value)}
                        placeholder="e.g. abc123def456…"
                    />
                </label>
                <div className="ib-form-card__actions">
                    <button
                        type="button"
                        className="ib-btn ib-btn--primary"
                        onClick={onManualConnect}
                        disabled={saving || !manualHash.trim()}
                    >
                        {saving ? 'Connecting…' : 'Connect'}
                    </button>
                </div>
            </div>
        );
    }

    if (connectMode === 'provision') {
        return (
            <ProvisionForm
                provisionForm={provisionForm}
                onProvisionFieldChange={onProvisionFieldChange}
                onProvision={onProvision}
                onBack={() => onSetConnectMode(null)}
                saving={saving}
            />
        );
    }

    return null;
}

function ProvisionForm({
    provisionForm,
    onProvisionFieldChange,
    onProvision,
    onBack,
    saving,
}: {
    provisionForm: Record<string, string>;
    onProvisionFieldChange: (field: string, value: string) => void;
    onProvision: () => void;
    onBack: () => void;
    saving: boolean;
}) {
    const { countries } = useIdibuCountries();

    return (
        <div className="ib-card ib-form-card">
            <div className="ib-form-card__header">
                <h3>Provision new account</h3>
                <button type="button" className="ib-btn ib-btn--ghost" onClick={onBack}>
                    <ChevronLeft size={14} /> Back
                </button>
            </div>
            <div className="ib-form-grid">
                <FormField label="Company name *" value={provisionForm.companyName} field="companyName" onChange={onProvisionFieldChange} />
                <FormField label="Admin first name *" value={provisionForm.adminFirstname} field="adminFirstname" onChange={onProvisionFieldChange} />
                <FormField label="Admin last name *" value={provisionForm.adminLastname} field="adminLastname" onChange={onProvisionFieldChange} />
                <FormField label="Admin email *" value={provisionForm.adminEmail} field="adminEmail" onChange={onProvisionFieldChange} type="email" />
                <FormField label="Username *" value={provisionForm.username} field="username" onChange={onProvisionFieldChange} placeholder="Login username (max 32 chars)" />
                <FormField label="Password *" value={provisionForm.password} field="password" onChange={onProvisionFieldChange} type="password" placeholder="Login password (max 32 chars)" />
                <label className="ib-field">
                    <span>Country</span>
                    <select
                        value={provisionForm.country}
                        onChange={(e) => onProvisionFieldChange('country', e.target.value)}
                    >
                        {countries.map(({ code, label }) => (
                            <option key={code} value={code}>{label}</option>
                        ))}
                    </select>
                </label>
                <FormField label="Postcode" value={provisionForm.postcode} field="postcode" onChange={onProvisionFieldChange} />
                <FormField label="Phone" value={provisionForm.phone} field="phone" onChange={onProvisionFieldChange} />
                <FormField label="Website" value={provisionForm.website} field="website" onChange={onProvisionFieldChange} placeholder="https://…" />
            </div>
            <div className="ib-form-card__actions">
                <button type="button" className="ib-btn ib-btn--primary" onClick={onProvision} disabled={saving}>
                    {saving ? 'Provisioning…' : 'Provision account'}
                </button>
            </div>
        </div>
    );
}

function FormField({
    label,
    value,
    field,
    onChange,
    type = 'text',
    placeholder,
}: {
    label: string;
    value: string;
    field: string;
    onChange: (field: string, value: string) => void;
    type?: string;
    placeholder?: string;
}) {
    return (
        <label className="ib-field">
            <span>{label}</span>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(field, e.target.value)}
                placeholder={placeholder}
            />
        </label>
    );
}

// ─── Styling ─────────────────────────────────────────────────────────────────
//
// Scoped styles for the panel (and reused by BoardMarketplace via shared
// `ib-` prefix). Token-driven — no hard-coded hex.

const PANEL_CSS = `
.ib-card {
    background: var(--surface-primary);
    border: 1px solid var(--border-default);
    border-radius: var(--radius-lg);
    padding: 16px 18px;
    margin-bottom: 18px;
}

.ib-card--placeholder {
    display: flex;
    align-items: center;
    gap: 10px;
    color: var(--text-secondary);
    font-size: 13px;
}

.ib-banner {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
    padding: 10px 14px;
    border-radius: var(--radius-md);
    font-size: 13px;
}

.ib-banner--error {
    background: var(--bg-danger);
    color: var(--fg-danger);
    border: 1px solid rgba(207, 34, 46, 0.18);
}

.ib-banner span {
    flex: 1;
    min-width: 0;
}

.ib-banner button {
    display: inline-flex;
    width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
    color: inherit;
    font-size: 16px;
    line-height: 1;
}

.ib-banner button:hover {
    background: rgba(0, 0, 0, 0.05);
}

.ib-status {
    display: flex;
    align-items: center;
    gap: 14px;
}

.ib-status__icon {
    display: grid;
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    place-items: center;
    border-radius: 999px;
    background: var(--bg-success);
    color: var(--fg-success);
}

.ib-status__icon--muted {
    background: var(--surface-tertiary);
    color: var(--text-tertiary);
}

.ib-status__copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}

.ib-status__copy strong {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    letter-spacing: -0.005em;
}

.ib-status__copy > span {
    color: var(--text-secondary);
    font-size: 12.5px;
    line-height: 1.5;
}

.ib-status__copy code {
    font-family: var(--v2-font-mono);
    font-size: 11.5px;
    padding: 1px 5px;
    background: var(--surface-tertiary);
    border-radius: var(--radius-sm);
    color: var(--text-primary);
}

.ib-tag {
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--accent-subtle);
    color: var(--accent-strong);
    font-size: 10.5px;
    font-weight: 500;
    letter-spacing: 0.02em;
}

.ib-status__test {
    display: inline-flex;
    align-items: center;
    margin-top: 4px;
    padding: 2px 8px;
    border-radius: var(--radius-sm);
    font-size: 12px;
    font-weight: 500;
}

.ib-status__test.ok { background: var(--bg-success); color: var(--fg-success); }
.ib-status__test.err { background: var(--bg-danger); color: var(--fg-danger); }

.ib-status__actions {
    display: inline-flex;
    flex-shrink: 0;
    align-items: center;
    gap: 8px;
}

.ib-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 30px;
    padding: 0 12px;
    border-radius: var(--radius-md);
    font-size: 12.5px;
    font-weight: 500;
    border: 1px solid transparent;
    background: transparent;
    color: var(--text-secondary);
    cursor: pointer;
    transition: background 100ms ease, border-color 100ms ease, color 100ms ease;
    white-space: nowrap;
}

.ib-btn:disabled { cursor: not-allowed; opacity: 0.55; }

.ib-btn--primary {
    background: var(--text-primary);
    color: var(--text-inverse);
}

.ib-btn--primary:hover:not(:disabled) { background: #111418; }

.ib-btn--ghost {
    border-color: var(--border-default);
    background: var(--surface-primary);
    color: var(--text-secondary);
}

.ib-btn--ghost:hover:not(:disabled) {
    background: var(--surface-hover);
    border-color: var(--border-emphasis);
    color: var(--text-primary);
}

.ib-btn--danger-ghost {
    border-color: rgba(207, 34, 46, 0.18);
    background: var(--surface-primary);
    color: var(--fg-danger);
}

.ib-btn--danger-ghost:hover:not(:disabled) {
    background: var(--bg-danger);
}

.ib-disconnected {
    display: grid;
    gap: 14px;
}

.ib-disconnected__copy {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ib-disconnected__copy strong {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
}

.ib-disconnected__copy span {
    color: var(--text-secondary);
    font-size: 12.5px;
    line-height: 1.5;
    max-width: 560px;
}

.ib-options {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
    gap: 10px;
}

.ib-option {
    display: grid;
    grid-template-columns: auto 1fr;
    grid-template-rows: auto auto;
    gap: 4px 12px;
    padding: 14px 16px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    text-align: left;
    cursor: pointer;
    transition: background 100ms ease, border-color 100ms ease;
}

.ib-option:hover {
    background: var(--surface-hover);
    border-color: var(--border-emphasis);
}

.ib-option__icon {
    grid-row: 1 / span 2;
    display: grid;
    width: 32px;
    height: 32px;
    place-items: center;
    border-radius: var(--radius-sm);
    background: var(--accent-subtle);
    color: var(--accent-strong);
}

.ib-option__title {
    color: var(--text-primary);
    font-size: 13px;
    font-weight: 600;
    align-self: end;
}

.ib-option__desc {
    color: var(--text-tertiary);
    font-size: 12px;
    line-height: 1.4;
    align-self: start;
}

.ib-form-card {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.ib-form-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.ib-form-card__header h3 {
    margin: 0;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
}

.ib-form-card__actions {
    display: flex;
    justify-content: flex-end;
}

.ib-form-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
}

.ib-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.ib-field > span {
    color: var(--text-secondary);
    font-size: 12px;
    font-weight: 500;
}

.ib-field input,
.ib-field select {
    width: 100%;
    height: 32px;
    padding: 0 10px;
    border: 1px solid var(--border-default);
    border-radius: var(--radius-md);
    background: var(--surface-primary);
    color: var(--text-primary);
    font-family: inherit;
    font-size: 13px;
    outline: none;
    transition: border-color 100ms ease, box-shadow 100ms ease;
}

.ib-field input:focus,
.ib-field select:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 3px var(--accent-subtle);
}

@media (max-width: 720px) {
    .ib-status,
    .ib-disconnected {
        flex-direction: column;
        align-items: stretch;
    }
    .ib-status__actions { justify-content: flex-end; }
    .ib-form-grid { grid-template-columns: 1fr; }
}
`;
